/**
 * «Счета юр.лицу» — §13.2 manager v3 (вкладка «Счета» владельца).
 *
 * Композиция по макету §13.2:
 *  - topbar: крошки «Финансы / Счета» + h2 «Счета юр.лицу» + CTA «Реквизиты»
 *    + «Запросить счёт»;
 *  - баннер плательщика (реквизиты: название / ИНН / КПП / счёт);
 *  - таблица счетов: Номер · Дата · Сумма · За что · Статус · Срок · PDF.
 *
 * Источник — GET /v1/invoices (тот же, что история §13.1). ВАЖНО: list-view
 * счёта на бэке #469 НЕ несёт counterparty/items/description — поэтому:
 *   - баннер реквизитов плательщика = ЗАГЛУШКА «реквизиты не заданы» (в
 *     /platform/me у тенанта нет ИНН/КПП/счёта; детальный счёт через
 *     GET /v1/invoices/{id} имеет counterparty, но это отдельный экран);
 *   - колонка «За что» выводится из периода (нет описания в списке);
 *   - «Реквизиты»/«Запросить счёт» — бэк-экшна нет → кнопки disabled (честно).
 *   - «PDF» — генерации нет → disabled.
 */
import { Button, Card, Skeleton } from '@hsc/ui';
import type { Invoice } from '@hsc/types';
import { useInvoices } from './useSubscription.js';
import { formatMoney } from './format.js';

const STATUS_LABELS: Record<string, string> = {
  draft: 'черновик',
  sent: 'выставлен',
  paid: 'оплачен',
  overdue: 'просрочен',
  canceled: 'отменён',
};

function rub(kopecks: number): string {
  return formatMoney(kopecks / 100, 'RUB');
}

function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

function period(iso: string): string {
  try {
    const s = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(
      new Date(iso),
    );
    return `Подписка ${s}`;
  } catch {
    return 'Подписка';
  }
}

function statusChipClass(state: string): string {
  if (state === 'paid') return 'ta-chip--done';
  if (state === 'overdue') return 'ta-chip--alert';
  if (state === 'sent') return 'ta-chip--pending';
  return 'ta-chip--muted';
}

function dueLabel(inv: Invoice): string {
  if (inv.state === 'paid' || inv.state === 'canceled') return '—';
  return inv.due_at ? `до ${shortDate(inv.due_at)}` : '—';
}

export function InvoicesLegalPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useInvoices();

  return (
    <section data-testid="invoices-legal-page">
      <div className="ta-pagehead bill-topbar">
        <div>
          <div className="ta-pagehead__crumbs">Финансы / Счета</div>
          <h2 className="ta-pagehead__title">Счета юр.лицу</h2>
        </div>
        <div className="bill-topbar__cta">
          <Button
            variant="ghost"
            size="sm"
            disabled
            title="Редактирование реквизитов появится позже"
            data-testid="invoices-legal-requisites"
          >
            Реквизиты
          </Button>
          <Button
            size="sm"
            disabled
            title="Запрос счёта появится позже"
            data-testid="invoices-legal-request"
          >
            Запросить счёт
          </Button>
        </div>
      </div>

      <div className="bill-body">
        {/* Баннер плательщика — реквизиты тенанта пока не задаются (заглушка). */}
        <Card className="bill-payer" data-testid="invoices-legal-payer">
          <span className="bill-payer__label">Плательщик:</span>{' '}
          <span className="ta-muted">реквизиты не заданы</span>
        </Card>

        {isLoading ? (
          <Card data-testid="invoices-legal-skeleton">
            <Skeleton height={18} radius={6} />
            <Skeleton height={18} radius={6} />
            <Skeleton height={18} radius={6} />
          </Card>
        ) : isError || data === undefined ? (
          <Card data-testid="invoices-legal-error">
            <p role="alert">Не удалось загрузить счета.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
          </Card>
        ) : data.invoices.length === 0 ? (
          <Card data-testid="invoices-legal-empty">
            <p className="ta-muted">Счетов ещё не было. Они появятся после первой подписки.</p>
          </Card>
        ) : (
          <Card className="bill-history">
            <table className="bill-table" data-testid="invoices-legal-table">
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Дата</th>
                  <th>Сумма</th>
                  <th>За что</th>
                  <th>Статус</th>
                  <th>Срок</th>
                  <th aria-label="Документ" />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoices-legal-row-${inv.id}`}>
                    <td>
                      <strong>{inv.number || inv.id.slice(0, 8)}</strong>
                    </td>
                    <td>{shortDate(inv.issued_at)}</td>
                    <td className="bill-table__money">{rub(inv.total_kopecks)}</td>
                    <td>{period(inv.issued_at)}</td>
                    <td>
                      <span className={`ta-chip ${statusChipClass(inv.state)}`}>
                        {STATUS_LABELS[inv.state] ?? inv.state}
                      </span>
                    </td>
                    <td>{dueLabel(inv)}</td>
                    <td className="bill-table__act">
                      <button
                        type="button"
                        className="bill-pdf"
                        disabled
                        title="Генерация PDF появится позже"
                      >
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </section>
  );
}
